import { useState, useEffect, FormEvent } from 'react';
import { getEvents, createEvent, updateEvent, deleteEvent, Event } from '../api/client';
import ImageUploader from '../components/ImageUploader';
import { normalizeUrlInput } from '../utils/url';
import { useAuth } from '../contexts/AuthContext';
import BodyField from '../components/BodyField';
import { previewText, richTextAllowed } from '../utils/richText';
import { previewRecurrence, WEEKDAYS, type Freq } from '../utils/recurrence';
import './Events.css';
import { overlayClose } from '../utils/overlayClose';

const EMPTY_REPEAT = { on: false, freq: 'weekly' as Freq, until: '', byWeekday: [] as number[] };

function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [imageUploading, setImageUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    description: '',
    image: '',
    url: '',
    linkText: '',
  });

  const { plan, features } = useAuth();
  const canRepeat = plan === 'club' || plan === 'pro';
  const [repeat, setRepeat] = useState(EMPTY_REPEAT);

  // Repeating only applies to a brand new event: editing an occurrence edits
  // that occurrence, and changing the pattern of an existing series would mean
  // deleting and rebuilding it.
  const showRepeat = !editingEvent;
  const preview = repeat.on
    ? previewRecurrence(formData.date, repeat.freq, repeat.until, repeat.byWeekday)
    : null;
  const repeatBlocks = Boolean(repeat.on && (preview?.problem || !preview?.count));

  const toggleWeekday = (d: number) => setRepeat(r => ({
    ...r,
    byWeekday: r.byWeekday.includes(d) ? r.byWeekday.filter(x => x !== d) : [...r.byWeekday, d],
  }));

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const data = await getEvents();
      setEvents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (event?: Event) => {
    if (event) {
      setEditingEvent(event);
      setFormData({
        title: event.title,
        date: event.date,
        time: event.time || '',
        location: event.location || '',
        description: event.description || '',
        image: event.image || '',
        url: event.url || '',
        linkText: event.linkText || '',
      });
    } else {
      setEditingEvent(null);
      setFormData({
        title: '',
        date: '',
        time: '',
        location: '',
        description: '',
        image: '',
        url: '',
        linkText: '',
      });
    }
    setRepeat(EMPTY_REPEAT);
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setRepeat(EMPTY_REPEAT);
    setEditingEvent(null);
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const payload: any = {
        title: formData.title,
        date: formData.date,
      };
      if (formData.time) payload.time = formData.time;
      if (formData.location) payload.location = formData.location;
      if (formData.description) payload.description = formData.description;
      payload.image = formData.image;
      payload.url = formData.url;
      payload.linkText = formData.linkText;

      if (repeat.on && !editingEvent) {
        payload.repeat = {
          freq: repeat.freq,
          until: repeat.until,
          ...(repeat.freq === 'weekly' && repeat.byWeekday.length
            ? { byWeekday: repeat.byWeekday } : {}),
        };
      }

      if (editingEvent) {
        await updateEvent(editingEvent.id, payload);
        setSuccessMessage('Event updated successfully');
      } else {
        const created: any = await createEvent(payload);
        setSuccessMessage(created?.count
          ? `${created.count} events created`
          : 'Event created successfully');
      }

      await loadEvents();
      handleCloseModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteEvent(deleteConfirmId);
      setSuccessMessage('Event deleted successfully');
      setDeleteConfirmId(null);
      await loadEvents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
      setDeleteConfirmId(null);
    }
  };


  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '';
    return timeStr;
  };

  if (isLoading) {
    return <div className="loading">Loading events...</div>;
  }

  return (
    <div className="events-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Events</h1>
          <p className="page-subtitle">Manage club events, matches, and sessions</p>
        </div>
        {events.length > 0 && (
          <button onClick={() => handleOpenModal()} className="btn btn-primary">
            Add Event
          </button>
        )}
      </div>

      {error && !showModal && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="events-list">
        {events.map((ev) => (
          <div key={ev.id} className="event-card">
            <div className="event-date-block">
              <span className="event-day">
                {new Date(ev.date).toLocaleDateString('en-GB', { day: 'numeric' })}
              </span>
              <span className="event-month">
                {new Date(ev.date).toLocaleDateString('en-GB', { month: 'short' })}
              </span>
              <span className="event-year">
                {new Date(ev.date).toLocaleDateString('en-GB', { year: 'numeric' })}
              </span>
            </div>

            <div className="event-info">
              <h3 className="event-title">{ev.title}</h3>

              <div className="event-meta">
                {ev.time && (
                  <span className="event-meta-item">
                    <span className="event-meta-icon">🕐</span>
                    {formatTime(ev.time)}
                  </span>
                )}
                {ev.location && (
                  <span className="event-meta-item">
                    <span className="event-meta-icon">📍</span>
                    {ev.location}
                  </span>
                )}
              </div>

              {ev.description && (
                <p className="event-description">{previewText(ev.description)}</p>
              )}
            </div>

            <div className="event-actions">
              <button
                onClick={() => handleOpenModal(ev)}
                className="btn btn-secondary btn-small"
              >
                Edit
              </button>
              <button
                onClick={() => setDeleteConfirmId(ev.id)}
                className="btn btn-danger btn-small"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <p className="empty-state-text">
            No events yet, add your first training session, match day, or social event
          </p>
          <button onClick={() => handleOpenModal()} className="btn btn-primary" style={{ marginTop: 16 }}>
            Add Event
          </button>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" {...overlayClose(handleCloseModal)}>
          <div className="modal-content event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingEvent ? 'Edit Event' : 'Add Event'}
              </h2>
              <button onClick={handleCloseModal} className="modal-close">
                &times;
              </button>
            </div>

            {error && <div className="error-message" style={{ margin: '0 0 16px' }}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title" className="form-label">Event Title *</label>
                <input
                  id="title"
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="e.g., Pre-Season Training, Home vs Riverside FC"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="date" className="form-label">Date *</label>
                  <input
                    id="date"
                    type="date"
                    className="form-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="time" className="form-label">Time</label>
                  <input
                    id="time"
                    type="time"
                    className="form-input"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  />
                </div>
              </div>

              {showRepeat && (
                <div className={`repeat-block${repeat.on ? ' is-open' : ''}`}>
                  <label className={`repeat-toggle${canRepeat ? '' : ' is-locked'}`}>
                    <input
                      type="checkbox"
                      checked={repeat.on}
                      disabled={!canRepeat}
                      onChange={(e) => setRepeat({ ...EMPTY_REPEAT, on: e.target.checked })}
                    />
                    <span className="repeat-toggle-text">Repeat this event</span>
                    {!canRepeat && <span className="repeat-lock-badge">Club &amp; Pro</span>}
                  </label>

                  {!canRepeat && (
                    <p className="repeat-hint">
                      Set up a weekly training session once instead of adding every date by hand.
                      Available on the Club and Pro plans.
                    </p>
                  )}

                  {repeat.on && canRepeat && (
                    <div className="repeat-options">
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="repeat-freq" className="form-label">Repeats</label>
                          <select
                            id="repeat-freq"
                            className="form-input"
                            value={repeat.freq}
                            onChange={(e) => setRepeat({ ...repeat, freq: e.target.value as Freq })}
                          >
                            <option value="daily">Every day</option>
                            <option value="weekly">Every week</option>
                            <option value="monthly">Every month</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label htmlFor="repeat-until" className="form-label">Until *</label>
                          <input
                            id="repeat-until"
                            type="date"
                            className="form-input"
                            value={repeat.until}
                            min={formData.date || undefined}
                            max={preview?.maxUntil || undefined}
                            onChange={(e) => setRepeat({ ...repeat, until: e.target.value })}
                            required
                          />
                        </div>
                      </div>

                      {repeat.freq === 'weekly' && (
                        <div className="form-group">
                          <label className="form-label">On these days</label>
                          <div className="repeat-days">
                            {WEEKDAYS.map(d => (
                              <button
                                type="button"
                                key={d.value}
                                className={`repeat-day${repeat.byWeekday.includes(d.value) ? ' is-on' : ''}`}
                                onClick={() => toggleWeekday(d.value)}
                              >
                                {d.short}
                              </button>
                            ))}
                          </div>
                          <p className="form-hint">
                            Leave blank to repeat on the same day of the week as the start date.
                          </p>
                        </div>
                      )}

                      {/* Live count, so nobody discovers the limit by being rejected. */}
                      {preview?.problem
                        ? <p className="repeat-problem">{preview.problem}</p>
                        : preview && preview.count > 0 && (
                          <p className="repeat-count">
                            Creates <strong>{preview.count}</strong> event{preview.count === 1 ? '' : 's'}.
                          </p>
                        )}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="location" className="form-label">Location</label>
                <input
                  id="location"
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Main Pitch, Town Hall Recreation Ground"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">Description</label>
                <BodyField
                  id="description"
                  value={formData.description}
                  rich={richTextAllowed(plan, features)}
                  compact
                  onChange={description => setFormData({ ...formData, description })}
                  rows={4}
                  placeholder="Any extra details for the event..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Image</label>
                <ImageUploader
                  onUpload={(url) => setFormData((f) => ({ ...f, image: url }))}
                  currentImage={formData.image}
                  onUploadingChange={setImageUploading}
                  label="Upload event image"
                />
                {formData.image && (
                  <button
                    type="button"
                    className="btn btn-outline btn-small"
                    style={{ marginTop: 8 }}
                    onClick={() => setFormData((f) => ({ ...f, image: '' }))}
                  >
                    Remove image
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="url" className="form-label">Link URL</label>
                  <input
                    id="url"
                    type="text"
                    className="form-input"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    onBlur={(e) => setFormData((f) => ({ ...f, url: normalizeUrlInput(e.target.value) }))}
                    placeholder="e.g. www.eventbrite.co.uk/..."
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="linkText" className="form-label">Link text</label>
                  <input
                    id="linkText"
                    type="text"
                    className="form-input"
                    value={formData.linkText}
                    onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                    placeholder="e.g., Register, More info"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSaving || imageUploading || repeatBlocks}
                >
                  {isSaving ? 'Saving...'
                    : imageUploading ? 'Uploading image…'
                    : editingEvent ? 'Update Event'
                    : preview?.count ? `Create ${preview.count} Events`
                    : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="modal-overlay" {...overlayClose(() => setDeleteConfirmId(null))}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Delete Event</h2>
              <button onClick={() => setDeleteConfirmId(null)} className="modal-close">
                &times;
              </button>
            </div>

            <p className="delete-confirm-text">
              Are you sure you want to delete this event? This cannot be undone.
            </p>

            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="btn btn-danger"
              >
                Delete Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;
